
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, Download, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Sample redemption data
const redemptions = [
  {
    id: 1,
    promotionTitle: "Summer Sale",
    code: "SUMMER25",
    customer: "John Doe",
    channel: "POS",
    date: "2025-05-10T14:35:42",
    discount: 12.50,
    products: ["Organic Green Apple", "Premium Mineral Water"]
  },
  {
    id: 2,
    promotionTitle: "Buy 1 Get 1 Free",
    code: "B1G1FREE",
    customer: "Mary Smith",
    channel: "JimatShop",
    date: "2025-05-11T09:22:15",
    discount: 3.99,
    products: ["Chocolate Cookies"]
  },
  {
    id: 3,
    promotionTitle: "Summer Sale",
    code: "SUMMER25",
    customer: "Jane Wilson",
    channel: "POS",
    date: "2025-05-11T16:44:30",
    discount: 5.00,
    products: ["Fresh Bread Loaf", "Premium Mineral Water"]
  },
  {
    id: 4,
    promotionTitle: "Weekend Special",
    code: "WEEKEND10",
    customer: "Robert Brown",
    channel: "JimatShop",
    date: "2025-05-12T10:15:22",
    discount: 8.75,
    products: ["Organic Green Apple", "Chocolate Cookies"]
  },
];

const RedeemReportsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [promotionFilter, setPromotionFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const filteredRedemptions = redemptions.filter(redemption => {
    const matchesSearch = 
      redemption.promotionTitle.toLowerCase().includes(searchTerm.toLowerCase()) || 
      redemption.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      redemption.customer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPromotion = promotionFilter === "all" || redemption.promotionTitle === promotionFilter;
    const matchesChannel = channelFilter === "all" || redemption.channel === channelFilter;
    
    // Simple date filter (just checking if the date starts with the filter value)
    const matchesDate = dateFilter === "" || redemption.date.startsWith(dateFilter);
    
    return matchesSearch && matchesPromotion && matchesChannel && matchesDate;
  });

  const uniquePromotions = Array.from(new Set(redemptions.map(r => r.promotionTitle)));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Redemption Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by promotion, code, or customer..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Select value={promotionFilter} onValueChange={setPromotionFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Promotions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Promotions</SelectItem>
                    {uniquePromotions.map((promotion) => (
                      <SelectItem key={promotion} value={promotion}>
                        {promotion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={channelFilter} onValueChange={setChannelFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Channels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    <SelectItem value="POS">POS</SelectItem>
                    <SelectItem value="JimatShop">JimatShop</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="relative">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-[150px]"
                  />
                  <Calendar className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Promotion</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Products</TableHead>
                    <TableHead>Discount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRedemptions.length > 0 ? (
                    filteredRedemptions.map((redemption) => (
                      <TableRow key={redemption.id}>
                        <TableCell className="font-medium">{redemption.promotionTitle}</TableCell>
                        <TableCell>{redemption.code}</TableCell>
                        <TableCell>{redemption.customer}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                            redemption.channel === 'POS' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                            {redemption.channel}
                          </span>
                        </TableCell>
                        <TableCell>{formatDate(redemption.date)}</TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate">
                            {redemption.products.join(", ")}
                          </div>
                        </TableCell>
                        <TableCell>${redemption.discount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                        No redemptions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm text-muted-foreground">
                  Showing {filteredRedemptions.length} of {redemptions.length} redemptions
                </span>
              </div>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Export Report
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download redemption report as CSV</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RedeemReportsTab;
